import React, { Component } from "react";
import PropTypes from "prop-types";
import { all } from "awaity/esm";
import { Radio, Row, Col, Statistic } from "antd";
const RadioGroup = Radio.Group;
import LoadingWrapper from "../common/loading-wrapper";
import { LineChart, DiscreteColorLegend, makeWidthFlexible } from "@unifiedpatents/euclid-react";
import { getEntityAppCounts, getEntityAppCountsByOneCondition, getAppCountsByOneCondition } from "../../services/api-prosecution";
import { upperCase } from "lodash";

const styles = {
  radioGroup: {
    width: "100%",
    maxHeight: 280,
    overflowY: "auto"
  },
  radio: {
    display: "block",
    textAlign: "left"
  },
  box: {
    textAlign: "center",
    backgroundColor: "#f6f6f6",
    padding: 8,
    borderRadius: 8
  }
};

const AllowanceRateChart = props => {
  const { data, dataArtUnit, xlabMap, width } = props;
  const getChartData = data => {
    let chartData = [];
    ["0", "1"].forEach(k => {
      if (data && data[k] && (data[k].Issued || 0) + (data[k].Abandoned || 0) > 0)
        chartData.push({
          k: xlabMap[k],
          v: (data[k].Issued || 0) / ((data[k].Issued || 0) + (data[k].Abandoned || 0))
        });
    });
    return chartData;
  };
  const toPercentage = v => `${(v * 100).toFixed(1)}%`;
  return (
    <LineChart
      width={width}
      height={160}
      margin={{ top: 8, left: 60, bottom: 40, right: 8 }}
      data={[
        { k: "Art Unit", v: getChartData(dataArtUnit) },
        { k: "Examiner", v: getChartData(data) }
      ]}
      domainY={[0, 1]}
      tickFormatY={toPercentage}
      tickTotalY={5}
      tooltipValueFormat={toPercentage}
    />
  );
};
AllowanceRateChart.propTypes = {
  data: PropTypes.object,
  dataArtUnit: PropTypes.object,
  xlabMap: PropTypes.object,
  width: PropTypes.number
};
const FlexibleAllowanceRateChart = makeWidthFlexible(AllowanceRateChart);

class AllowanceRates extends Component {
  static propTypes = {
    name: PropTypes.string.isRequired,
    field: PropTypes.string.isRequired
  };

  state = {
    // entity counts
    countsByArtUnit: {},
    countsByFinalRejection: {},
    countsByInterview: {},
    countsByAppeal: {},
    countsByRCE: {},
    // uspto counts
    countsArtUnitByFinalRejection: {},
    countsArtUnitByInterview: {},
    countsArtUnitByAppeal: {},
    countsArtUnitByRCE: {},
    // select
    selectedArtUnit: "all",
    loading: false
  };

  componentDidMount() {
    this._isMounted = true;
    this._getEntityAppCounts(this.props.name, this.props.field);
    this._getArtUnitAppCounts();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.name !== this.props.name) {
      this._getEntityAppCounts(nextProps.name, nextProps.field);
      this._resetSelectedArtUnit();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  render() {
    const {
      countsByArtUnit,
      countsByFinalRejection,
      countsByInterview,
      countsByAppeal,
      countsByRCE,
      countsArtUnitByFinalRejection,
      countsArtUnitByInterview,
      countsArtUnitByAppeal,
      countsArtUnitByRCE,
      selectedArtUnit,
      loading
    } = this.state;

    // counts bar for selected art unit
    const counts = {
      Issued: (countsByArtUnit[selectedArtUnit] && countsByArtUnit[selectedArtUnit].Issued) || 0,
      Abandoned: (countsByArtUnit[selectedArtUnit] && countsByArtUnit[selectedArtUnit].Abandoned) || 0,
      Pending: (countsByArtUnit[selectedArtUnit] && countsByArtUnit[selectedArtUnit].Pending) || 0
    };
    counts.total = counts.Issued + counts.Abandoned + counts.Pending;
    // title map
    const typeMap = {
      total: "Total Apps",
      Issued: "Issued",
      Abandoned: "Abandoned",
      Pending: "Pending"
    };
    // xlab maps
    const xlabMapFinalRejection = {
      "0": "Before Final Rejection",
      "1": "After Final Rejection"
    };
    const xlabMapInterview = {
      "0": "Without Interview",
      "1": "With Interview"
    };
    const xlabMapAppeal = {
      "0": "Without Appeal",
      "1": "With Appeal"
    };
    const xlabMapRCE = {
      "0": "Without RCE",
      "1": "With RCE"
    };
    // legends
    const legends = [selectedArtUnit === "all" ? "USPTO" : `Art Unit ${upperCase(selectedArtUnit)}`, upperCase(this.props.name)];
    return (
      <div style={{ display: "flex", flexDirection: "row", alignItems: "stretch", width: "100%", gap: 16 }}>
        <LoadingWrapper loading={loading} style={{ ...styles.box, width: 120 }} className="hidden-sm">
          <h4>{"Art Units"}</h4>
          <RadioGroup onChange={this._onSelectArtUnit} value={selectedArtUnit} style={styles.radioGroup}>
            {Object.keys(countsByArtUnit)
              .reverse()
              .map((au, i) => (
                <Radio value={au} key={i} style={styles.radio}>
                  {upperCase(au)}
                </Radio>
              ))}
          </RadioGroup>
        </LoadingWrapper>
        <LoadingWrapper loading={loading} style={{ flex: 1 }}>
          <Row gutter={8}>
            {["total", "Issued", "Abandoned", "Pending"].map((t, i) => (
              <Col span={24} sm={12} lg={6} key={i}>
                <Statistic title={typeMap[t]} value={counts[t]} style={styles.box} />
              </Col>
            ))}
          </Row>
          <Row gutter={8} style={{ margin: "8px 0 0 0" }}>
            <Col span={24} lg={12} xxl={6} style={styles.box}>
              <h3>{"Effects of Final Rejection"}</h3>
              <div><FlexibleAllowanceRateChart data={countsByFinalRejection[selectedArtUnit]} dataArtUnit={countsArtUnitByFinalRejection[selectedArtUnit]} xlabMap={xlabMapFinalRejection} /></div>
              <DiscreteColorLegend orientation="horizontal" items={legends} />
            </Col>
            <Col span={24} lg={12} xxl={6} style={styles.box}>
              <h3>{"Effects of Interview"}</h3>
              <div><FlexibleAllowanceRateChart data={countsByInterview[selectedArtUnit]} dataArtUnit={countsArtUnitByInterview[selectedArtUnit]} xlabMap={xlabMapInterview} /></div>
              <DiscreteColorLegend orientation="horizontal" items={legends} />
            </Col>
            <Col span={24} lg={12} xxl={6} style={styles.box}>
              <h3>{"Effects of Appeal"}</h3>
              <div><FlexibleAllowanceRateChart data={countsByAppeal[selectedArtUnit]} dataArtUnit={countsArtUnitByAppeal[selectedArtUnit]} xlabMap={xlabMapAppeal} /></div>
              <DiscreteColorLegend orientation="horizontal" items={legends} />
            </Col>
            <Col span={24} lg={12} xxl={6} style={styles.box}>
              <h3>{"Effects of RCE"}</h3>
              <div><FlexibleAllowanceRateChart data={countsByRCE[selectedArtUnit]} dataArtUnit={countsArtUnitByRCE[selectedArtUnit]} xlabMap={xlabMapRCE} /></div>
              <DiscreteColorLegend orientation="horizontal" items={legends} />
            </Col>
          </Row>
        </LoadingWrapper>
      </div>
    );
  }

  _getEntityAppCounts = (name, field) => {
    if (this._isMounted) this.setState({ loading: true });
    all([
      //TODO: reduce number of requests
      getEntityAppCountsByOneCondition(name, "examiner_art_unit", false, field),
      getEntityAppCountsByOneCondition(name, "has_final_rejection", true, field),
      getEntityAppCountsByOneCondition(name, "has_interview", true, field),
      getEntityAppCountsByOneCondition(name, "has_appeal", true, field),
      getEntityAppCountsByOneCondition(name, "has_RCE", true, field),
      // for total `{all: ...}`
      getEntityAppCounts(name, field),
      getEntityAppCountsByOneCondition(name, "has_final_rejection", false, field),
      getEntityAppCountsByOneCondition(name, "has_interview", false, field),
      getEntityAppCountsByOneCondition(name, "has_appeal", false, field),
      getEntityAppCountsByOneCondition(name, "has_RCE", false, field)
    ])
      .then(([dataAU, dataFR, dataI, dataA, dataRCE, dataAll, dataAllFR, dataAllI, dataAllA, dataAllRCE]) => {
        if (this._isMounted)
          this.setState({
            countsByArtUnit: {
              all: dataAll,
              ...dataAU
            },
            countsByFinalRejection: {
              all: dataAllFR,
              ...dataFR
            },
            countsByInterview: {
              all: dataAllI,
              ...dataI
            },
            countsByAppeal: {
              all: dataAllA,
              ...dataA
            },
            countsByRCE: {
              all: dataAllRCE,
              ...dataRCE
            },
            loading: false
          });
      })
      .catch(() => {
        if (this._isMounted)
          this.setState({
            loading: false
          });
      });
  };

  _getArtUnitAppCounts = () => {
    all([
      //TODO: reduce number of requests
      getAppCountsByOneCondition("has_final_rejection", true),
      getAppCountsByOneCondition("has_interview", true),
      getAppCountsByOneCondition("has_appeal", true),
      getAppCountsByOneCondition("has_RCE", true),
      // for total `{all: ...}`
      getAppCountsByOneCondition("has_final_rejection", false),
      getAppCountsByOneCondition("has_interview", false),
      getAppCountsByOneCondition("has_appeal", false),
      getAppCountsByOneCondition("has_RCE", false)
    ])
      .then(([dataFR, dataI, dataA, dataRCE, dataAllFR, dataAllI, dataAllA, dataAllRCE]) => {
        if (this._isMounted)
          this.setState({
            countsArtUnitByFinalRejection: {
              all: dataAllFR,
              ...dataFR
            },
            countsArtUnitByInterview: {
              all: dataAllI,
              ...dataI
            },
            countsArtUnitByAppeal: {
              all: dataAllA,
              ...dataA
            },
            countsArtUnitByRCE: {
              all: dataAllRCE,
              ...dataRCE
            }
          });
      })
      .catch(() => {
        if (this._isMounted)
          this.setState({
            loading: false
          });
      });
  };

  _onSelectArtUnit = e => {
    if (this._isMounted)
      this.setState({
        selectedArtUnit: e.target.value
      });
  };

  _resetSelectedArtUnit = () => {
    if (this._isMounted)
      this.setState({
        selectedArtUnit: "all"
      });
  };
}

export default AllowanceRates;
